"use client";

import { useState } from "react";
import { useToolUi } from "@/components/tools/tool-ui";
import { writeToClipboard } from "@/lib/clipboard";

const CODE_A_LOWER = 97;
const CODE_A_UPPER = 65;
const CODE_ZERO = 48;

/**
 * Builds a lookup map for a Mathematical Alphanumeric Symbols style from the
 * block's starting code points. Digits are optional — Unicode defines no
 * italic or bold-italic digits.
 */
function buildAlphabetMap(
  lowerStart: number,
  upperStart: number,
  digitStart?: number
): Map<string, string> {
  const map = new Map<string, string>();
  for (let i = 0; i < 26; i++) {
    map.set(
      String.fromCharCode(CODE_A_LOWER + i),
      String.fromCodePoint(lowerStart + i)
    );
    map.set(
      String.fromCharCode(CODE_A_UPPER + i),
      String.fromCodePoint(upperStart + i)
    );
  }
  if (digitStart !== undefined) {
    for (let i = 0; i < 10; i++) {
      map.set(
        String.fromCharCode(CODE_ZERO + i),
        String.fromCodePoint(digitStart + i)
      );
    }
  }
  return map;
}

// Sans-serif bold: 𝗮 / 𝗔 / 𝟬
const SANS_BOLD_MAP = buildAlphabetMap(0x1_d5_ee, 0x1_d5_d4, 0x1_d7_ec);
// Serif bold: 𝐚 / 𝐀 / 𝟎
const SERIF_BOLD_MAP = buildAlphabetMap(0x1_d4_1a, 0x1_d4_00, 0x1_d7_ce);
// Sans-serif italic: 𝘢 / 𝘈 (no italic digits exist)
const SANS_ITALIC_MAP = buildAlphabetMap(0x1_d6_22, 0x1_d6_08);
// Sans-serif bold italic: 𝙖 / 𝘼 (no bold-italic digits exist)
const SANS_BOLD_ITALIC_MAP = buildAlphabetMap(0x1_d6_56, 0x1_d6_3c);

function transform(text: string, map: Map<string, string>): string {
  return [...text].map((ch) => map.get(ch) ?? ch).join("");
}

function ResultCard({
  label,
  value,
  note,
}: {
  label: string;
  value: string;
  note?: string;
}) {
  const strings = useToolUi(BOLD_TEXT_GENERATOR_STRINGS);
  const [copied, setCopied] = useState(false);

  function handleCopy() {
    if (value.length === 0) {
      return;
    }
    writeToClipboard(value).then((copied) => {
      if (!copied) {
        return;
      }

      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <div className="flex items-center justify-between gap-2">
        <p className="font-medium text-foreground text-sm">{label}</p>
        <button
          className="rounded-md border border-border px-3 py-1 text-muted-foreground text-xs transition-colors hover:bg-accent hover:text-foreground disabled:opacity-40"
          disabled={value.length === 0}
          onClick={handleCopy}
          type="button"
        >
          {copied ? strings.copied : strings.copy}
        </button>
      </div>
      <p className="mt-2 min-h-6 whitespace-pre-wrap break-words text-foreground text-sm leading-relaxed">
        {value.length > 0 ? (
          value
        ) : (
          <span className="text-muted-foreground">
            {strings.typeAboveToSeeOutput}
          </span>
        )}
      </p>
      {note && <p className="mt-2 text-muted-foreground text-xs">{note}</p>}
    </div>
  );
}

/** English strings. A translated content file overrides these through `ui`. */
export const BOLD_TEXT_GENERATOR_STRINGS = {
  inputText: "Input Text",
  typeOrPasteYourText: "Type or paste your text here...",
  results: "Results",
  boldSansSerif: "Bold (sans-serif, 𝗮𝗯𝗰)",
  serifBold: "Serif bold (𝐚𝐛𝐜)",
  italic: "Italic (𝘢𝘣𝘤)",
  boldItalic: "Bold italic (𝙖𝙗𝙘)",
  aNoteOnAccessibility: "A note on accessibility",
  theseAreSpecialUnicodeCharacters:
    "These are special Unicode characters, not fonts or formatting. Screen readers may read them out letter by letter, mispronounce them, or skip them entirely, and they can break text search and spell checking. Avoid them in accessible or professional documents — use real bold and italic formatting in your word processor instead.",
  copied: "Copied!",
  copy: "Copy",
  typeAboveToSeeOutput: "Type above to see output…",
  noItalicDigitsNote:
    "Unicode defines no italic digits, so numbers stay regular.",
  noBoldItalicDigitsNote:
    "Unicode defines no bold-italic digits, so numbers stay regular.",
} as const;

export default function BoldTextGeneratorWidget() {
  const [input, setInput] = useState("");

  const strings = useToolUi(BOLD_TEXT_GENERATOR_STRINGS);

  return (
    <>
      {/* Input */}
      <div className="space-y-2">
        <label
          className="font-medium text-foreground text-sm"
          htmlFor="bold-input"
        >
          {strings.inputText}
        </label>
        <textarea
          className="min-h-[140px] w-full resize-y rounded-lg border border-border bg-card p-4 text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          id="bold-input"
          onChange={(e) => setInput(e.target.value)}
          placeholder={strings.typeOrPasteYourText}
          value={input}
        />
      </div>
      {/* Results */}
      <section className="space-y-4">
        <h2 className="font-semibold text-foreground text-xl">
          {strings.results}
        </h2>
        <div className="space-y-3">
          <ResultCard
            label={strings.boldSansSerif}
            value={transform(input, SANS_BOLD_MAP)}
          />
          <ResultCard
            label={strings.serifBold}
            value={transform(input, SERIF_BOLD_MAP)}
          />
          <ResultCard
            label={strings.italic}
            note={strings.noItalicDigitsNote}
            value={transform(input, SANS_ITALIC_MAP)}
          />
          <ResultCard
            label={strings.boldItalic}
            note={strings.noBoldItalicDigitsNote}
            value={transform(input, SANS_BOLD_ITALIC_MAP)}
          />
        </div>
      </section>
      {/* Caveat */}
      <section className="rounded-lg border border-border bg-muted/30 p-5">
        <h2 className="font-semibold text-base text-foreground">
          {strings.aNoteOnAccessibility}
        </h2>
        <p className="mt-2 text-muted-foreground text-sm leading-relaxed">
          {strings.theseAreSpecialUnicodeCharacters}
        </p>
      </section>
    </>
  );
}
